/**
 * Building blocks for a Google-style week time grid: the hours shown, and where each timed
 * booking sits in its day column (overlapping bookings side by side).
 */

export interface TimedEvent {
  id: string;
  /** Minutes from midnight. */
  start: number;
  end: number;
  [key: string]: any;
}

export interface LaidOutEvent extends TimedEvent {
  /** Which of `columns` side-by-side lanes the booking takes. */
  column: number;
  columns: number;
}

const minutes = (time: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})/.exec(time || '');
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

/** Whole hours from the practice's start to its end, stretched to take in every booking. */
export function timeGridHours(startTime: string, endTime: string, events: { start: number; end: number }[]): { startMinutes: number; endMinutes: number } {
  let start = minutes(startTime) ?? 7 * 60;
  let end = minutes(endTime) ?? 19 * 60;
  if (end <= start) end = Math.min(24 * 60, start + 12 * 60);
  for (const e of events) {
    start = Math.min(start, e.start);
    end = Math.max(end, e.end);
  }
  return {
    startMinutes: Math.max(0, Math.floor(start / 60) * 60),
    endMinutes: Math.min(24 * 60, Math.ceil(end / 60) * 60),
  };
}

/**
 * Lanes for a day's bookings: each joins the first lane free at its start; a group of bookings
 * that overlap one another shares the width between the lanes the group needed.
 */
export function layoutDayEvents<T extends TimedEvent>(events: T[]): (T & LaidOutEvent)[] {
  const sorted = [...events].sort((a, b) => a.start - b.start || b.end - a.end);
  const out: (T & LaidOutEvent)[] = [];
  let group: (T & LaidOutEvent)[] = [];
  let laneEnds: number[] = [];
  let groupEnd = -1;

  const closeGroup = () => {
    group.forEach(e => e.columns = laneEnds.length);
    group = [];
    laneEnds = [];
  };

  for (const event of sorted) {
    if (event.start >= groupEnd) closeGroup();
    let lane = laneEnds.findIndex(end => end <= event.start);
    if (lane < 0) lane = laneEnds.length;
    laneEnds[lane] = Math.max(event.end, event.start + 1);
    const placed = { ...event, column: lane, columns: 1 };
    group.push(placed);
    out.push(placed);
    groupEnd = Math.max(groupEnd, laneEnds[lane]);
  }
  closeGroup();
  return out;
}
